import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { ICardData, ICardModel, IHttpResponse } from '@partners/api';
import { IRemoteError } from '@partners/errors';
import { HttpService, tapResult, tapStatus } from '@partners/http';
import { TFeature } from '@partners/settings';
import { CardStore } from '@partners/state';
import { takeOnce } from '@partners/utils';
import { Observable, catchError } from 'rxjs';
import { NavigationAdapter } from '../../navigation/services/navigation-adapter.service';
import { CardSeverities } from './errors/card.errors';

@Injectable({
  providedIn: 'root'
})
export class CardFacade {
  private _httpService = inject(HttpService);
  private _navAdapter = inject(NavigationAdapter);
  private _severityHandlers = inject(CardSeverities);
  private _cardStore = inject(CardStore);

  readonly cardModel = computed<ICardModel>(() => this._cardStore.model$$());

  /**
   * True when the last card submit was rejected with W_0824 - a blocking debit card error, as opposed to the
   * I_0264 status message. the debit card alert hides "continue anyway" in that case
   */
  private readonly _isDebitCardBlocked$$ = signal<boolean>(false);
  readonly isDebitCardBlocked$$: Signal<boolean> = this._isDebitCardBlocked$$.asReadonly();

  /** a signal holding card details (used to display on card-label component) */
  readonly cardFormat = computed<ICardData>(() => {
    const cardData: ICardData = this._cardStore.data$$();

    if (!cardData) {
      return undefined;
    }
    const { issuerCardExpirationDate, issuerCardLast4Digits, issuerCardBrandName } = cardData;

    return {
      issuerCardExpirationDate,
      issuerCardLast4Digits,
      issuerCardBrandName
    };
  });

  /**
   * @param model the card details to submit
   * @param nextFeature the feature to move to when the response carries no screenName. supplied by
   * the card alerts, which are not part of the stage graph and hence cannot continue by moveNext.
   * when it is not supplied - the process continues by the stage graph
   */
  sendCard(model?: ICardModel, nextFeature?: TFeature): Observable<IHttpResponse<ICardData>> {
    this._isDebitCardBlocked$$.set(false);
    return this._httpService.post<ICardModel, ICardData>(`card`, model).pipe(
      takeOnce(),
      catchError((error: IRemoteError) => {
        this._isDebitCardBlocked$$.set(error?.severity?.code === 'W_0824');
        return this._severityHandlers.inspect(error);
      }),
      tapResult((data: ICardData) => {
        this.dispatchCardSubmitSuccess({ model });
      }),
      tapStatus(({ screenName, messageCode }) => {
        if (messageCode === 'I_0264') {
          this._navAdapter.toAlert('debit-card-alert');
          return;
        }
        if (messageCode === 'I_0277') {
          this._navAdapter.toAlert('cal-card-alert');
          return;
        }
        if (screenName) {
          this._navAdapter.moveTo(screenName as TFeature);
          return;
        }
        if (nextFeature) {
          this._navAdapter.moveTo(nextFeature);
          return;
        }
        this._navAdapter.moveNext();
      })
    );
  }

  dispatchCardSubmitSuccess({ model }: { model: ICardModel }) {
    this._cardStore.dispatchCardSubmitSuccess({ model });
  }
}
